import { LazyLayout, LoadingScreen } from '@nohara-dxdev/react-core';
import { OidcCallbackHandler, OidcLoginRedirect } from '@nohara-dxdev/react-oidc';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { paths } from '@/config/paths';

import { default as OrgRoot, ErrorBoundary as OrgErrorBoundary } from './routes/org/root';
import AppRoot, { ErrorBoundary as AppErrorBoundary } from './routes/root';

const convert = (queryClient: QueryClient) => (m: any) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m;
  return {
    ...rest,
    loader: clientLoader?.(queryClient),
    action: clientAction?.(queryClient),
    Component,
  };
};

export const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      path: paths.auth.callback.path,
      element: (
        <OidcCallbackHandler
          successPath={paths.home.path}
          errorPath={paths.error.path}
          element={<LoadingScreen />}
        />
      ),
    },
    {
      path: paths.auth.login.path,
      element: <OidcLoginRedirect element={<LoadingScreen />} />,
    },
    {
      path: paths.auth.logout.path,
      element: <OidcLoginRedirect element={<LoadingScreen />} />,
    },
    {
      path: paths.home.path,
      element: <AppRoot />,
      ErrorBoundary: AppErrorBoundary,
      children: [
        {
          path: '',
          element: <Navigate to={paths.org.root.path} />,
        },
        {
          path: paths.org.root.path,
          element: <OrgRoot />,
          ErrorBoundary: OrgErrorBoundary,
          children: [
            {
              path: paths.onboarding.path,
              HydrateFallback: LoadingScreen,
              lazy: () => import('./routes/org/onboarding').then(convert(queryClient)),
            },
            {
              path: paths.org.userProfile.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/org/user').then(convert(queryClient)),
            },
            {
              path: paths.org.selectedOrg.path,
              children: [
                {
                  path: paths.org.dashboard.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/dashboard').then(convert(queryClient)),
                },
                {
                  path: paths.org.userProfile.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/user').then(convert(queryClient)),
                },
                {
                  path: paths.org.users.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/users').then(convert(queryClient)),
                },
                {
                  path: paths.org.roles.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/roles').then(convert(queryClient)),
                },
                {
                  path: paths.org.activity.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/activity').then(convert(queryClient)),
                },
                {
                  path: paths.org.user.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/user').then(convert(queryClient)),
                },
                {
                  path: paths.org.projectsList.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/projects').then(convert(queryClient)),
                },
                {
                  path: paths.org.orgProfile.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/org').then(convert(queryClient)),
                },
                {
                  path: paths.org.itemTemplates.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/item-templates').then(convert(queryClient)),
                },
                {
                  path: paths.org.itemTemplate.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/item-template').then(convert(queryClient)),
                },
                {
                  path: paths.org.itemTemplateSchema.path,
                  HydrateFallback: LazyLayout,
                  lazy: () =>
                    import('./routes/org/item-template-schema').then(convert(queryClient)),
                },
                {
                  path: paths.org.settings.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/settings').then(convert(queryClient)),
                },
                {
                  path: paths.org.project.root.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/project/root').then(convert(queryClient)),
                  children: [
                    {
                      path: paths.org.project.dashboard.path,
                      HydrateFallback: LazyLayout,
                      lazy: () =>
                        import('./routes/org/project/dashboard').then(convert(queryClient)),
                    },
                    {
                      path: paths.org.project.users.path,
                      HydrateFallback: LazyLayout,
                      lazy: () => import('./routes/org/project/users').then(convert(queryClient)),
                    },
                    {
                      path: paths.org.project.user.path,
                      HydrateFallback: LazyLayout,
                      lazy: () => import('./routes/org/user').then(convert(queryClient)),
                    },
                    {
                      path: paths.org.project.buildings.path,
                      HydrateFallback: LazyLayout,
                      lazy: () =>
                        import('./routes/org/project/buildings').then(convert(queryClient)),
                    },
                    {
                      path: paths.org.project.building.path,
                      HydrateFallback: LazyLayout,
                      lazy: () =>
                        import('./routes/org/project/building').then(convert(queryClient)),
                    },
                    {
                      path: paths.org.project.systems.path,
                      HydrateFallback: LazyLayout,
                      lazy: () => import('./routes/org/project/systems').then(convert(queryClient)),
                    },
                    {
                      path: paths.org.project.room.path,
                      HydrateFallback: LazyLayout,
                      lazy: () => import('./routes/org/project/room').then(convert(queryClient)),
                    },
                    {
                      path: paths.org.project.system.path,
                      HydrateFallback: LazyLayout,
                      lazy: () => import('./routes/org/project/system').then(convert(queryClient)),
                    },
                    {
                      path: paths.org.project.reports.path,
                      HydrateFallback: LazyLayout,
                      lazy: () => import('./routes/org/project/reports').then(convert(queryClient)),
                    },
                    {
                      path: paths.org.project.settings.path,
                      HydrateFallback: LazyLayout,
                      lazy: () =>
                        import('./routes/org/project/settings').then(convert(queryClient)),
                    },
                    {
                      path: paths.org.project.activity.path,
                      HydrateFallback: LazyLayout,
                      lazy: () =>
                        import('./routes/org/project/activity').then(convert(queryClient)),
                    },
                    {
                      path: paths.org.project.powerTypes.path,
                      HydrateFallback: LazyLayout,
                      lazy: () =>
                        import('./routes/org/project/power-types').then(convert(queryClient)),
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: '*',
      HydrateFallback: LoadingScreen,
      lazy: () => import('./routes/not-found').then(convert(queryClient)),
    },
  ]);

export const AppRouter = () => {
  const queryClient = useQueryClient();

  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  return <RouterProvider router={router} />;
};
